/**
 * Trigger button component rendered inside the Shadow DOM.
 *
 * Renders a "Try On" button that opens the try-on modal when clicked.
 * Visual customisation is available via CSS custom properties on the host element.
 *
 * CSS class prefix is configurable via `cssPrefix` (default: 'mb-'). This
 * allows each widget to use its own namespace while sharing structural code.
 */

/** Options for constructing the trigger button. */
export interface TriggerButtonOptions {
  /** Button label text. Defaults to "Try On". */
  label?: string;
  /** Called when the button is clicked. */
  onClick: () => void;
  /** CSS class prefix. Defaults to 'mb-'. */
  cssPrefix?: string;
  /** Accessible label. Defaults to "<label> -- virtual try-on". */
  ariaLabel?: string;
  /** SVG path markup for the leading icon. Defaults to glasses. */
  iconSvg?: string;
}

const DEFAULT_TRIGGER_ICON = `
  <path d="M2 10h4"/>
  <path d="M18 10h4"/>
  <circle cx="8" cy="13" r="3"/>
  <circle cx="16" cy="13" r="3"/>
  <path d="M11 13h2"/>
`;

/**
 * Build the trigger button element and attach the click handler.
 *
 * Returns the button element -- caller is responsible for appending it to the
 * Shadow DOM container.
 *
 * @param options - Label, click callback, and CSS prefix.
 * @returns The constructed button element.
 */
export function createTriggerButton(options: TriggerButtonOptions): HTMLButtonElement {
  const {
    label = 'Try On',
    onClick,
    cssPrefix = 'mb-',
    ariaLabel = `${label} -- virtual try-on`,
    iconSvg = DEFAULT_TRIGGER_ICON,
  } = options;
  const p = cssPrefix;

  const btn = document.createElement('button');
  btn.className = `${p}trigger-btn`;
  btn.type = 'button';
  btn.setAttribute('aria-label', ariaLabel);

  // Inline SVG keeps the shared button self-contained across Shadow DOM widgets.
  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('viewBox', '0 0 24 24');
  icon.setAttribute('fill', 'none');
  icon.setAttribute('stroke', 'currentColor');
  icon.setAttribute('stroke-width', '2');
  icon.setAttribute('stroke-linecap', 'round');
  icon.setAttribute('stroke-linejoin', 'round');
  icon.setAttribute('aria-hidden', 'true');
  icon.classList.add(`${p}trigger-btn__icon`);
  icon.innerHTML = iconSvg;

  const labelSpan = document.createElement('span');
  labelSpan.textContent = label;

  btn.appendChild(icon);
  btn.appendChild(labelSpan);
  btn.addEventListener('click', onClick);

  return btn;
}

/**
 * Update the button's disabled state (e.g. while the modal is loading).
 *
 * @param btn      - The button element returned by createTriggerButton.
 * @param disabled - Whether the button should be non-interactive.
 */
export function setTriggerButtonDisabled(btn: HTMLButtonElement, disabled: boolean): void {
  btn.disabled = disabled;
  btn.setAttribute('aria-busy', String(disabled));
}
